"use client";

// # FILE: src/shared/components/error-boundary.tsx
// # VERSION: 1.0.0
// # START_MODULE_CONTRACT:
// # PURPOSE: Глобальный компонент Error Boundary для перехвата и обработки ошибок React.
// # SCOPE: Обработка ошибок рендеринга React компонентов.
// # INPUT: Дочерние компоненты.
// # OUTPUT: UI для отображения ошибок.
// # KEYWORDS: [DOMAIN(9): ErrorHandling; TECH(8): React; PATTERN(7): ErrorBoundary]
// # END_MODULE_CONTRACT

import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertCircle, RefreshCcw, Home } from "lucide-react";
import { Button } from "@oxpa/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@oxpa/shared/ui/card";
import Link from "next/link";

// START_INTERFACE_Props
// START_CONTRACT:
// PURPOSE: Пропсы для ErrorBoundary компонента.
// END_CONTRACT
interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetOnPropsChange?: boolean;
}
// END_INTERFACE_Props

// START_INTERFACE_State
// START_CONTRACT:
// PURPOSE: Состояние ErrorBoundary компонента.
// END_CONTRACT
interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}
// END_INTERFACE_State

// START_CLASS_ErrorBoundary
// START_CONTRACT:
// PURPOSE: Классический React Error Boundary для перехвата ошибок рендеринга.
// ATTRIBUTES:
// - [Состояние ошибки] => state: State
// METHODS:
// - [Получение производного состояния от ошибки] => getDerivedStateFromError
// - [Компонент перехватил ошибку] => componentDidCatch
// - [Сброс состояния ошибки] => resetError
// KEYWORDS: [PATTERN(9): ErrorBoundary; CONCEPT(8): ErrorHandling]
// END_CONTRACT
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  // START_METHOD_getDerivedStateFromError
  // START_CONTRACT:
  // PURPOSE: Обновляет состояние при возникновении ошибки.
  // INPUTS:
  // - [Ошибка] => error: Error
  // OUTPUTS:
  // - State - Новое состояние с информацией об ошибке
  // END_CONTRACT
  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }
  // END_METHOD_getDerivedStateFromError

  // START_METHOD_componentDidCatch
  // START_CONTRACT:
  // PURPOSE: Вызывается при перехвате ошибки. Логирует ошибку.
  // INPUTS:
  // - [Ошибка] => error: Error
  // - [Информация об ошибке] => errorInfo: ErrorInfo
  // SIDE_EFFECTS:
  // - Логирование ошибки в консоль
  // - Вызов колбэка onError если передан
  // END_CONTRACT
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // START_BLOCK_LOG_ERROR
    console.error("[ErrorBoundary] Ошибка перехвачена:", error);
    console.error("[ErrorBoundary] Информация о компоненте:", errorInfo);
    // END_BLOCK_LOG_ERROR

    this.setState({
      errorInfo,
    });

    // START_BLOCK_CALLBACK
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
    // END_BLOCK_CALLBACK
  }
  // END_METHOD_componentDidCatch

  // START_METHOD_resetError
  // START_CONTRACT:
  // PURPOSE: Сбрасывает состояние ошибки для повторной попытки рендеринга.
  // END_CONTRACT
  resetError = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };
  // END_METHOD_resetError

  // START_METHOD_handleReload
  // START_CONTRACT:
  // PURPOSE: Перезагружает страницу.
  // END_CONTRACT
  handleReload = (): void => {
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };
  // END_METHOD_handleReload

  render(): ReactNode {
    // START_BLOCK_RENDER_ERROR
    if (this.state.hasError) {
      // Если передан кастомный fallback, используем его
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Стандартный UI ошибки
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <Card className="max-w-lg w-full">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
              <CardTitle className="text-xl text-red-600">
                Произошла ошибка
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-600 text-center">
                К сожалению, произошла непредвиденная ошибка. Мы уже работаем
                над её устранением.
              </p>

              {this.state.error && (
                <div className="bg-gray-100 rounded-lg p-4 text-sm">
                  <p className="font-medium text-red-600 mb-1">
                    Технические детали:
                  </p>
                  <p className="text-gray-700 font-mono break-all">
                    {this.state.error.message}
                  </p>
                  {this.state.errorInfo && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
                        Подробности компонента
                      </summary>
                      <pre className="mt-2 text-xs text-gray-600 overflow-auto max-h-40">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                <Button
                  onClick={this.resetError}
                  variant="outline"
                  className="gap-2"
                >
                  <RefreshCcw className="h-4 w-4" />
                  Попробовать снова
                </Button>
                <Button onClick={this.handleReload} className="gap-2">
                  <RefreshCcw className="h-4 w-4" />
                  Обновить страницу
                </Button>
                <Link href="/" passHref>
                  <Button variant="ghost" className="gap-2">
                    <Home className="h-4 w-4" />
                    На главную
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }
    // END_BLOCK_RENDER_ERROR

    return this.props.children;
  }
}
// END_CLASS_ErrorBoundary

// START_FUNCTION_withErrorBoundary
// START_CONTRACT:
// PURPOSE: HOC для оборачивания компонента в ErrorBoundary.
// INPUTS:
// - [Компонент] => Component: React.ComponentType<P>
// - [Опции ErrorBoundary] => options: Partial<Props>
// OUTPUTS:
// - React.FC<P> - Компонент, обернутый в ErrorBoundary
// END_CONTRACT
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  options: Partial<Omit<Props, "children">> = {}
): React.FC<P> {
  const WrappedComponent: React.FC<P> = (props) => (
    <ErrorBoundary {...options}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
}
// END_FUNCTION_withErrorBoundary
